<script lang="ts">
	import Layout from "../components/Layout.svelte";
	import { getGlobalSettings } from "../getGlobalSettings.js";
	import type { SolutionsProps } from "./SolutionsProps.js";
	import { categoryNameMap, Category, categoryTextMap, categoryAboutMap } from "../../../../core/schema/Category.js";
	import StaticPicture from "../../../shared/components/Picture/StaticPicture.svelte";
	import racksMd from "../assets/images/solutions/racks.jpg?webp&h=500&w=608&imagetools";
	import racksLg from "../assets/images/solutions/racks.jpg?webp&h=710&w=864&imagetools";
	import racksXl from "../assets/images/solutions/racks.jpg?webp&h=520&w=630&imagetools";
	import cranesMd from "../assets/images/solutions/cranes.png?webp&h=500&w=608&imagetools";
	import cranesLg from "../assets/images/solutions/cranes.png?webp&h=710&w=864&imagetools";
	import cranesXl from "../assets/images/solutions/cranes.png?webp&h=520&w=630&imagetools";
	import robotsMd from "../assets/images/solutions/robots.png?webp&h=500&w=608&imagetools";
	import robotsLg from "../assets/images/solutions/robots.png?webp&h=710&w=864&imagetools";
	import robotsXl from "../assets/images/solutions/robots.png?webp&h=520&w=630&imagetools";
	import vertinodeAiMd from "../assets/images/solutions/vertinode.jpg?webp&h=500&w=608&imagetools";
	import vertinodeAiLg from "../assets/images/solutions/vertinode.jpg?webp&h=710&w=864&imagetools";
	import vertinodeAiXl from "../assets/images/solutions/vertinode.jpg?webp&h=520&w=630&imagetools";
	import arrowRightLong from "../../../../core/assets/icons/arrow-right-long.svg?raw";
	import Link from "../../../shared/components/Link.svelte";
	import { getSolutionSubcategoryPath } from "../../../../core/schema/paths/getSolutionSubcategoryPath.js";
	import { nbspify } from "../../../../core/utils/nbspify.js";
	import type { SourceSetQuery } from "../../../shared/components/Picture/SourceSetQuery.js";
	import { vertiTypeTargetFromSourceTenantIdMappings } from "../../../../core/schema/vertiTypeTargetFromSourceTenantIdMappings.js";
	import { VertiType } from "../../../../core/schema/VertiType.js";
	import { getTranslate } from "../utils/getTranslate.js";
	import translations from "./Solutions.translations.json";
	import { categoryAnchorsMap } from "../../../../core/schema/paths/categoryAnchorMap";

	type $$Props = SolutionsProps;

	const translate = getTranslate(translations);
	const { TENANT_LOCALE, TENANT_ID, VERTINODE_WEB_URL } = getGlobalSettings();
	const { categories, vertinodeAiProduct } = $$restProps as $$Props;

	const categoryImageMap = {
		[Category.Cranes]: {
			"<lg": cranesMd,
			">lg": cranesLg,
			">xl": cranesXl,
		},
		[Category.Racks]: {
			"<lg": racksMd,
			">lg": racksLg,
			">xl": racksXl,
		},
		[Category.Robots]: {
			"<lg": robotsMd,
			">lg": robotsLg,
			">xl": robotsXl,
		},
		[Category.VertinodeAi]: {
			"<lg": vertinodeAiMd,
			">lg": vertinodeAiLg,
			">xl": vertinodeAiXl,
		},
	} satisfies Record<Category, Partial<Record<SourceSetQuery, string>>>;
</script>

<Layout>
	<div class="container">
		{#each categories as category, index}
			<section
				class="mb-14 flex flex-col gap-5 md:p-8 lg:mb-0 lg:gap-8 xl:flex-row"
				class:xl:!flex-row-reverse={index % 2 !== 0}
				id={categoryAnchorsMap[category]}
				class:mt-0={index === 0}
			>
				<div class="h-min rounded-lg bg-gray-300 md:p-4 xl:h-[34.5rem] xl:w-1/2">
					<StaticPicture
						image={categoryImageMap[category][">xl"]}
						alt={categoryNameMap[category][TENANT_LOCALE]}
						width={632}
						height={520}
						loading={index <= 1 ? "eager" : "lazy"}
						class="relative h-full w-full items-center justify-center overflow-hidden xl:flex xl:h-[32.5rem]"
						imgClass="rounded-lg h-full w-full object-cover inset-0 max-w-[initial] xl:absolute"
						sourcesets={categoryImageMap[category]}
					/>
				</div>
				<div class="flex flex-col items-start justify-center xl:h-[34.5rem] xl:w-1/2 xl:pl-8 xl:pr-[7rem]">
					{#if index === 0}
						<h1 class="m-0 mb-4 p-0 font-normal lg:mb-8 xl:text-xl">
							{nbspify(categoryNameMap[category][TENANT_LOCALE])}
						</h1>
					{:else}
						<h2 class="m-0 mb-4 p-0 font-normal lg:mb-8 xl:text-xl">
							{nbspify(categoryNameMap[category][TENANT_LOCALE])}
						</h2>
					{/if}
					<p class="text-2xs mb-8 leading-5 xl:mb-16">{nbspify(categoryTextMap[category][TENANT_LOCALE])}</p>
					<Link
						asButton
						variant="primary"
						class="w-auto"
						icon={arrowRightLong}
						href="/{getSolutionSubcategoryPath(category, vertinodeAiProduct)[TENANT_LOCALE]}"
					>
						{nbspify(categoryAboutMap[category][TENANT_LOCALE])}
					</Link>
					{#if category === Category.VertinodeAi}
						<Link
							asButton
							variant="primary"
							class="mt-3 flex w-auto"
							icon={arrowRightLong}
							href={VERTINODE_WEB_URL[
								vertiTypeTargetFromSourceTenantIdMappings[VertiType.Node][VertiType.Flex][TENANT_ID]
							]}
						>
							{nbspify(translate("toVertiNode"))}
						</Link>
					{/if}
				</div>
			</section>
		{/each}
	</div>
</Layout>
